import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Users, MessageSquare, Wand2, FileSpreadsheet, BookOpen,
  RefreshCw, Mail, Trash2, Plus, Save, Download, Image as ImageIcon, Loader2, Send, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, setAdminToken, getAdminToken, API } from "../lib/adminApi";

const TABS = [
  { key: "stats", label: "Statistiche", icon: FileSpreadsheet },
  { key: "leads", label: "Leads", icon: Users },
  { key: "contacts", label: "Contatti", icon: Mail },
  { key: "chats", label: "Chat", icon: MessageSquare },
  { key: "viz", label: "Ristrutturazioni AI", icon: Wand2 },
  { key: "training", label: "Addestra MurgiAI", icon: BookOpen },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("stats");

  React.useEffect(() => {
    if (!getAdminToken()) { navigate("/albertoadimpannello"); return; }
    adminApi.get("/admin/me").catch(() => navigate("/albertoadimpannello"));
  }, [navigate]);

  const logout = () => { setAdminToken(null); navigate("/albertoadimpannello"); };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      <header className="bg-brand-dark text-brand-paper">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-brand-terracotta text-white flex items-center justify-center font-bold heading-display">EM</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-paper/60">Admin</div>
              <div className="heading-display text-lg leading-none">Edil Murgia · MurgiAI</div>
            </div>
          </div>
          <button onClick={logout} className="text-sm flex items-center gap-2 text-brand-paper/70 hover:text-white" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <nav className="flex flex-wrap gap-2 mb-6 sticky top-0 bg-brand-bg py-3 z-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all flex items-center gap-2 ${
                  active ? "bg-brand-terracotta text-white border-brand-terracotta" : "bg-white text-brand-ink border-brand-line hover:border-brand-ink/40"
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </nav>

        {tab === "stats" && <StatsPanel />}
        {tab === "leads" && <LeadsPanel />}
        {tab === "contacts" && <ContactsPanel />}
        {tab === "chats" && <ChatsPanel />}
        {tab === "viz" && <VisualizationsPanel />}
        {tab === "training" && <TrainingPanel />}
      </div>
    </div>
  );
}

// ---------------- Stats ----------------
function StatsPanel() {
  const [stats, setStats] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try { const res = await adminApi.get("/admin/stats"); setStats(res.data); } catch (e) { toast.error("Errore caricamento"); }
  };
  React.useEffect(() => { load(); }, []);

  const runJob = async (dry) => {
    if (!dry && !window.confirm("Inviare email recap a tutti i lead di 30+ giorni e PURGARE le immagini? L'azione non è reversibile.")) return;
    setBusy(true);
    try {
      const res = await adminApi.post(`/admin/job/recap-and-cleanup?dry_run=${dry ? "true" : "false"}`);
      const d = res.data;
      toast.success(`Job: ${d.candidates_to_send} candidati · ${d.sent_results.filter(r=>r.ok).length} inviate · ${d.purged_images} immagini purgate`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore job");
    } finally { setBusy(false); }
  };

  if (!stats) return <Loading />;

  const cards = [
    { k: "leads", label: "Leads email raccolti", v: stats.leads, icon: Users, color: "bg-brand-terracotta/10 text-brand-terracotta" },
    { k: "contacts", label: "Richieste contatto", v: stats.contacts, icon: Mail, color: "bg-brand-sage/15 text-brand-sage" },
    { k: "sessions", label: "Sessioni chat", v: stats.chat_sessions, icon: MessageSquare, color: "bg-amber-100 text-amber-700" },
    { k: "messages", label: "Messaggi totali", v: stats.chat_messages, icon: MessageSquare, color: "bg-amber-50 text-amber-600" },
    { k: "viz", label: "Ristrutturazioni AI generate", v: stats.visualizations, icon: Wand2, color: "bg-violet-50 text-violet-700" },
    { k: "pending", label: "Email recap in attesa", v: stats.visualizations_pending_email, icon: Send, color: "bg-rose-50 text-rose-700" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.k} className="card-tactile p-6" data-testid={`stat-${c.k}`}>
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${c.color}`}><Icon className="h-5 w-5" /></div>
              <div className="heading-display text-4xl mt-4">{c.v}</div>
              <div className="text-sm text-brand-mute mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="card-tactile p-6 md:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-1" />
          <div>
            <h3 className="heading-display text-xl">Job email recap & pulizia 30 giorni</h3>
            <p className="text-sm text-brand-mute mt-2 leading-relaxed max-w-2xl">
              Invia email "Prima/Dopo" a tutti gli utenti che hanno provato il visualizzatore 30+ giorni fa e poi
              elimina le immagini dal database. Suggerimento: esegui prima un "Dry run" per vedere quante email
              verrebbero inviate.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={() => runJob(true)} disabled={busy} className="btn-secondary" data-testid="job-dryrun">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Dry run (anteprima)
              </button>
              <button onClick={() => runJob(false)} disabled={busy} className="btn-primary" data-testid="job-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Esegui ora
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Leads ----------------
function LeadsPanel() {
  const [rows, setRows] = React.useState([]);
  React.useEffect(() => { adminApi.get("/admin/leads").then((r) => setRows(r.data)).catch(() => toast.error("Errore")); }, []);
  return (
    <div>
      <Toolbar title={`${rows.length} leads`} onExport={() => downloadCSV("leads")} testid="leads" />
      <Table cols={["Email", "Sorgente", "Data"]} rows={rows.map(r => [r.email, r.source, fmt(r.created_at)])} testid="leads-table" />
    </div>
  );
}

// ---------------- Contacts ----------------
function ContactsPanel() {
  const [rows, setRows] = React.useState([]);
  React.useEffect(() => { adminApi.get("/admin/contacts").then((r) => setRows(r.data)).catch(() => toast.error("Errore")); }, []);
  return (
    <div>
      <Toolbar title={`${rows.length} richieste`} onExport={() => downloadCSV("contacts")} testid="contacts" />
      <Table
        cols={["Nome", "Email", "Telefono", "Messaggio", "Data"]}
        rows={rows.map(r => [r.name, r.email, r.phone || "—", (r.message || "").slice(0, 80) + (r.message?.length > 80 ? "..." : ""), fmt(r.created_at)])}
        testid="contacts-table"
      />
    </div>
  );
}

// ---------------- Chats ----------------
function ChatsPanel() {
  const [rows, setRows] = React.useState([]);
  const [thread, setThread] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = () => adminApi.get("/admin/chats").then((r) => setRows(r.data)).catch(() => toast.error("Errore"));
  React.useEffect(() => { load(); }, []);

  const openThread = async (sid) => {
    setBusy(true);
    try { const r = await adminApi.get(`/admin/chats/${encodeURIComponent(sid)}`); setThread({ sid, messages: r.data }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Toolbar title={`${rows.length} sessioni di chat`} testid="chats" />
      <div className="card-tactile overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg text-xs uppercase tracking-widest text-brand-mute">
            <tr><th className="text-left py-3 px-4">Sessione</th><th className="text-left">Messaggi</th><th className="text-left">Anteprima</th><th className="text-left">Ultimo</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.session_id} className="border-t border-brand-line">
                <td className="py-3 px-4 font-mono text-xs">{r.session_id.slice(0, 14)}…</td>
                <td>{r.messages}</td>
                <td className="max-w-md truncate text-brand-mute">{r.preview}</td>
                <td className="text-brand-mute text-xs">{fmt(r.last_at)}</td>
                <td className="text-right pr-4">
                  <button onClick={() => openThread(r.session_id)} className="text-brand-terracotta text-xs font-semibold hover:underline" data-testid={`chat-open-${r.session_id.slice(0,8)}`}>
                    Apri →
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="p-6 text-center text-brand-mute">Nessuna sessione ancora.</td></tr>}
          </tbody>
        </table>
      </div>
      {thread && (
        <Modal onClose={() => setThread(null)} title={`Sessione ${thread.sid.slice(0, 18)}…`}>
          {busy && <Loading />}
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {thread.messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-brand-terracotta text-white rounded-br-md" : "bg-brand-bg border border-brand-line rounded-bl-md"
                }`}>{m.content}</div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------- Visualizations ----------------
function VisualizationsPanel() {
  const [rows, setRows] = React.useState([]);
  const [busyId, setBusyId] = React.useState(null);
  const [zoom, setZoom] = React.useState(null);
  const load = () => adminApi.get("/admin/visualizations").then((r) => setRows(r.data)).catch(() => toast.error("Errore"));
  React.useEffect(() => { load(); }, []);

  const sendOne = async (id) => {
    if (!window.confirm("Inviare ora la mail recap a questa email?")) return;
    setBusyId(id);
    try {
      await adminApi.post(`/admin/visualizations/${id}/send`);
      toast.success("Email inviata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusyId(null); }
  };

  const imgUrl = (id, kind) => `${API}/admin/visualizations/${id}/image?kind=${kind}`;
  const imgWithToken = (id, kind) => imgUrl(id, kind); // token via header in fetch — we use blob fetch

  return (
    <div>
      <Toolbar title={`${rows.length} ristrutturazioni AI`} testid="viz" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="card-tactile p-4" data-testid={`viz-card-${r.id.slice(0,8)}`}>
            <div className="grid grid-cols-2 gap-2">
              <AuthImage url={imgWithToken(r.id, "original")} alt="Prima" testid={`viz-before-${r.id.slice(0,8)}`} onClick={() => setZoom({ url: imgWithToken(r.id, "original"), label: "Prima" })} disabled={!r.has_images} />
              <AuthImage url={imgWithToken(r.id, "result")} alt="Dopo" testid={`viz-after-${r.id.slice(0,8)}`} onClick={() => setZoom({ url: imgWithToken(r.id, "result"), label: "Dopo" })} disabled={!r.has_images} />
            </div>
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="text-sm">
                <div className="font-semibold truncate">{r.email || <span className="text-brand-mute italic">no email</span>}</div>
                <div className="text-xs text-brand-mute mt-0.5">Stile: {r.style} · {fmt(r.created_at)}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${r.email_sent ? "bg-brand-sage/15 text-brand-sage" : "bg-amber-100 text-amber-700"}`}>
                {r.email_sent ? "Inviata" : "Da inviare"}
              </span>
            </div>
            <button
              disabled={!r.email || r.email_sent || busyId === r.id}
              onClick={() => sendOne(r.id)}
              className="mt-3 w-full btn-secondary justify-center text-sm py-2 disabled:opacity-50"
              data-testid={`viz-send-${r.id.slice(0,8)}`}
            >
              {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Invia mail recap
            </button>
          </div>
        ))}
        {!rows.length && <div className="col-span-full p-10 text-center text-brand-mute">Nessuna ristrutturazione ancora.</div>}
      </div>
      {zoom && (
        <Modal onClose={() => setZoom(null)} title={zoom.label} wide>
          <AuthImage url={zoom.url} alt={zoom.label} className="w-full h-auto rounded-xl" />
        </Modal>
      )}
    </div>
  );
}

function AuthImage({ url, alt, className, onClick, disabled, testid }) {
  const [src, setSrc] = React.useState(null);
  const [error, setError] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    if (disabled) { setError(true); return; }
    fetch(url, { headers: { Authorization: `Bearer ${getAdminToken()}` } })
      .then((r) => { if (!r.ok) throw new Error(r.statusText); return r.blob(); })
      .then((b) => { if (active) setSrc(URL.createObjectURL(b)); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; if (src) URL.revokeObjectURL(src); };
    // eslint-disable-next-line
  }, [url, disabled]);
  if (error) return <div className={`aspect-[4/3] rounded-lg bg-brand-bg flex items-center justify-center text-brand-mute text-xs ${className||""}`}><ImageIcon className="h-5 w-5" /></div>;
  if (!src) return <div className={`aspect-[4/3] rounded-lg bg-brand-bg animate-pulse ${className||""}`} />;
  return <img src={src} alt={alt} onClick={onClick} className={`rounded-lg ${onClick ? "cursor-zoom-in" : ""} ${className || "w-full h-full object-cover aspect-[4/3]"}`} data-testid={testid} />;
}

// ---------------- Training ----------------
function TrainingPanel() {
  const [data, setData] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const load = () => adminApi.get("/admin/training").then((r) => setData(r.data)).catch(() => toast.error("Errore"));
  React.useEffect(() => { load(); }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));
  const updateFaq = (idx, key, val) => setData((d) => {
    const faq = [...(d.faq || [])];
    faq[idx] = { ...faq[idx], [key]: val };
    return { ...d, faq };
  });
  const addFaq = () => setData((d) => ({ ...d, faq: [...(d.faq || []), { question: "", answer: "" }] }));
  const delFaq = (idx) => setData((d) => ({ ...d, faq: d.faq.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.post("/admin/training", { ai_name: data.ai_name, system_prompt: data.system_prompt, faq: data.faq });
      toast.success("Addestramento salvato. MurgiAI userà il nuovo prompt da subito.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare il prompt di default? Le FAQ saranno cancellate.")) return;
    await adminApi.post("/admin/training/reset");
    toast.success("Reset effettuato");
    load();
  };

  if (!data) return <Loading />;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="card-tactile p-6 md:p-8">
        <h3 className="heading-display text-xl">Identità dell'assistente</h3>
        <p className="text-sm text-brand-mute mt-1">Personalizza nome e personalità del chatbot.</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-4 items-start">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brand-mute">Nome AI</span>
            <input value={data.ai_name} onChange={(e) => update({ ai_name: e.target.value })} className="mt-1 w-full rounded-xl border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-terracotta" data-testid="training-ai-name" />
          </label>
        </div>
      </div>

      <div className="card-tactile p-6 md:p-8">
        <h3 className="heading-display text-xl">Prompt di sistema</h3>
        <p className="text-sm text-brand-mute mt-1">Il "cervello" base. Definisce tono, regole e info aziendali. Modificalo con cautela.</p>
        <textarea
          value={data.system_prompt}
          onChange={(e) => update({ system_prompt: e.target.value })}
          rows={14}
          className="mt-3 w-full font-mono text-xs rounded-xl border border-brand-line bg-white px-3 py-3 focus:outline-none focus:border-brand-terracotta"
          data-testid="training-prompt"
        />
      </div>

      <div className="card-tactile p-6 md:p-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="heading-display text-xl">FAQ — Knowledge Base</h3>
            <p className="text-sm text-brand-mute mt-1">Domande &amp; risposte specifiche. MurgiAI le userà come fonte primaria.</p>
          </div>
          <button onClick={addFaq} className="btn-secondary text-sm py-2" data-testid="training-add-faq"><Plus className="h-4 w-4" /> Aggiungi</button>
        </div>
        <div className="mt-4 space-y-3">
          {(data.faq || []).map((f, i) => (
            <div key={i} className="border border-brand-line rounded-xl p-4 bg-brand-bg/50" data-testid={`training-faq-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <span className="text-xs font-bold text-brand-mute">FAQ #{i + 1}</span>
                <button onClick={() => delFaq(i)} className="text-rose-600 hover:text-rose-800 text-xs flex items-center gap-1" data-testid={`training-del-faq-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Elimina
                </button>
              </div>
              <input
                value={f.question}
                onChange={(e) => updateFaq(i, "question", e.target.value)}
                placeholder="Domanda (es. Quanto costa il cappotto termico?)"
                className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-terracotta"
              />
              <textarea
                value={f.answer}
                onChange={(e) => updateFaq(i, "answer", e.target.value)}
                rows={3}
                placeholder="Risposta che MurgiAI dovrebbe usare..."
                className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-terracotta"
              />
            </div>
          ))}
          {!(data.faq || []).length && <div className="text-sm text-brand-mute italic p-4 text-center">Nessuna FAQ. Aggiungine una per iniziare ad addestrare MurgiAI.</div>}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="training-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva addestramento
        </button>
        <button onClick={reset} className="btn-secondary" data-testid="training-reset"><RefreshCw className="h-4 w-4" /> Reset default</button>
      </div>
    </div>
  );
}

// ---------------- Helpers ----------------
function Toolbar({ title, onExport, testid }) {
  return (
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="heading-display text-2xl">{title}</div>
      {onExport && (
        <button onClick={onExport} className="btn-secondary text-sm py-2" data-testid={`${testid}-export`}>
          <Download className="h-4 w-4" /> Esporta CSV
        </button>
      )}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="card-tactile overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-brand-bg text-xs uppercase tracking-widest text-brand-mute">
          <tr>{cols.map((c) => <th key={c} className="text-left py-3 px-4">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-brand-line align-top">
              {r.map((cell, j) => <td key={j} className="py-3 px-4">{cell}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length} className="p-6 text-center text-brand-mute">Nessun risultato.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`card-tactile p-6 ${wide ? "max-w-4xl" : "max-w-2xl"} w-full max-h-[90vh] overflow-y-auto bg-white`}>
        <div className="flex items-start justify-between mb-4">
          <h4 className="heading-display text-xl">{title}</h4>
          <button onClick={onClose} className="text-brand-mute hover:text-brand-ink">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Loading() { return <div className="p-10 text-center text-brand-mute"><Loader2 className="h-5 w-5 animate-spin inline" /></div>; }

function fmt(iso) { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } }

async function downloadCSV(kind) {
  try {
    const res = await fetch(`${API}/admin/export/${kind}`, { headers: { Authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new Error(res.statusText);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}.csv`;
    a.click();
  } catch (e) { toast.error("Errore export"); }
}
